<!--
  Marketplace card: photo, then price, name, and where/when - what a buyer scans for. The whole
  card opens the item (/s/:id, a stretched link on the name); Share, Save and Chat sit above that link.
  The description stays on the item page.
-->
<article
  class="sale-card group relative flex h-full flex-col overflow-hidden rounded-xl bg-white shadow-sm ring-1 ring-black/5 transition-shadow hover:shadow-md focus-within:ring-2 focus-within:ring-primary"
  [class.is-sold]="isSold"
>
  <!-- A fixed square: the photo is positioned inside it, so a tall or wide photo is cropped
       (object-cover) rather than stretching the box - and with it, the whole row of cards. -->
  <div class="relative aspect-square overflow-hidden bg-primary/5">
    <img
      *ngIf="coverImage && !coverFailed; else noPhoto"
      [src]="coverThumbnail"
      [attr.srcset]="coverSrcset"
      sizes="(min-width: 640px) 16rem, 50vw"
      [alt]="sale.item_name"
      loading="lazy"
      decoding="async"
      class="absolute inset-0 h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.03]"
      [class.grayscale]="isSold"
      (error)="coverFailed = true"
    />
    <ng-template #noPhoto>
      <div class="absolute inset-0 flex items-center justify-center text-primary/30" aria-hidden="true">
        <mat-icon class="!h-12 !w-12 !text-5xl">image</mat-icon>
      </div>
    </ng-template>

    <div class="absolute left-2 top-2 flex flex-wrap gap-1">
      <span *ngIf="sale.is_admin_post" class="rounded bg-amber-100 px-1.5 py-0.5 text-xs font-medium text-amber-800">Admin</span>
      <span *ngIf="condition" class="rounded bg-white/90 px-1.5 py-0.5 text-xs font-medium text-primary shadow-sm">{{ condition }}</span>
    </div>

    <div *ngIf="isSold" class="absolute inset-0 flex items-center justify-center bg-black/40">
      <span class="rounded-md bg-white px-3 py-1 text-sm font-bold uppercase tracking-widest text-primary">Sold</span>
    </div>

    <span
      *ngIf="photoCount > 1"
      class="absolute bottom-2 left-2 flex items-center gap-1 rounded-full bg-black/60 px-2 py-0.5 text-xs font-medium text-white"
      [attr.aria-label]="photoCount + ' photos'"
    >
      <mat-icon class="!h-auto !w-auto !text-sm" aria-hidden="true">photo_library</mat-icon>
      {{ photoCount }}
    </span>

    <button
      *ngIf="!isSold"
      type="button"
      class="absolute right-2 top-2 z-10 flex h-9 w-9 items-center justify-center rounded-full bg-white/90 text-primary shadow-sm transition hover:bg-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary"
      [attr.aria-label]="'Share ' + sale.item_name"
      (click)="saleActions.share(sale)"
    >
      <mat-icon class="!h-auto !w-auto !text-xl">share</mat-icon>
    </button>
  </div>

  <div class="flex flex-1 flex-col gap-0.5 p-3">
    <!-- The name is the heading (and the link) - it comes first for screen readers; `order` puts the price above it. -->
    <h3 class="order-2 m-0 truncate text-sm font-medium text-gray-900">
      <a [routerLink]="['/s', sale.slug_id ?? sale.id]" class="after:absolute after:inset-0 focus:outline-none">{{ sale.item_name }}</a>
    </h3>
    <p class="order-1 m-0 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-lg font-bold leading-tight text-primary" [class.line-through]="isSold" [class.opacity-60]="isSold">
      &#8358;{{ sale._price }}
      <span *ngIf="sale.minimum_price && !isSold" class="rounded bg-accent/70 px-1.5 py-0.5 text-[11px] font-medium leading-none no-underline">Negotiable</span>
    </p>
    <p class="order-3 m-0 truncate text-xs text-gray-500">
      <ng-container *ngIf="sale.CorpMember?._location">{{ sale.CorpMember?._location | saleLocation }} · </ng-container>
      <time [attr.datetime]="sale.created_at">{{ sale._age }}</time>
    </p>

    <!--
      TODO(wachukxs/gh#116): show a "Verified corper" label/symbol next to the seller's name, for
      sellers who've been verified.
      TODO(wachukxs/gh#116): a verified email (email_verified_at) is NOT enough to call someone a
      verified corper - it only proves they own the inbox, and every Google sign-up has it.
      Verification will be an NYSC ID card upload, reviewed by an admin.
    -->
    <p *ngIf="sellerName && !isOwn" class="order-4 m-0 flex min-w-0 items-center gap-1.5 pt-1 text-xs text-gray-600">
      <span class="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary text-[10px] font-bold text-accent" aria-hidden="true">{{ sellerName[0] | uppercase }}</span>
      <span class="truncate">{{ sellerName }}</span>
    </p>

    <div *ngIf="(isNearYou || leaving) && !isSold && !isOwn" class="order-5 flex flex-wrap gap-1 pt-1">
      <span *ngIf="isNearYou" class="flex items-center gap-0.5 rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-medium text-primary">
        <mat-icon class="!h-auto !w-auto !text-xs" aria-hidden="true">near_me</mat-icon>Near you
      </span>
      <span *ngIf="leaving" class="flex items-center gap-0.5 rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-800">
        <mat-icon class="!h-auto !w-auto !text-xs" aria-hidden="true">schedule</mat-icon>{{ leaving }}
      </span>
    </div>

    <!-- A buyer's actions: Save next to Chat. -->
    <div *ngIf="!isOwn && !isSold" class="order-6 mt-auto flex items-center gap-2 pt-2">
      <button
        type="button"
        class="relative z-10 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-primary/30 text-primary transition hover:bg-primary/5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary disabled:opacity-50"
        [attr.aria-label]="'Save ' + sale.item_name"
        [attr.aria-pressed]="saved"
        [disabled]="isSaving"
        (click)="toggleSave()"
      >
        <mat-icon class="!h-auto !w-auto !text-xl">{{ saved ? 'bookmark' : 'bookmark_border' }}</mat-icon>
      </button>
      <button
        type="button"
        class="relative z-10 h-9 flex-1 rounded-lg bg-primary text-sm font-medium text-white transition hover:bg-primary-light focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
        [attr.aria-label]="'Chat the seller about ' + sale.item_name"
        (click)="saleActions.chatWithSeller(sale)"
      >
        Chat seller
      </button>
    </div>

    <!-- Your own item outside the dashboard: say why there's no Save or Chat, and where to manage it. -->
    <p *ngIf="isOwn && !ownerActions" class="order-6 m-0 mt-auto pt-2 text-xs text-gray-500">
      Your item ·
      <a routerLink="/dashboard" [queryParams]="{ post: 'sale_' + sale.id }" class="relative z-10 font-medium text-primary underline">Manage</a>
    </p>

    <!-- The seller's actions, where they manage their posts (the dashboard). -->
    <div *ngIf="isOwn && ownerActions" class="order-6 mt-auto flex items-center gap-2 pt-2">
      <button
        type="button"
        class="relative z-10 h-9 flex-1 rounded-lg border border-primary/30 text-sm font-medium text-primary transition hover:bg-primary/5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary disabled:opacity-50"
        [disabled]="isUpdatingSold"
        (click)="toggleSold()"
      >
        {{ isSold ? 'Back on sale' : 'Mark as sold' }}
      </button>
      <button
        type="button"
        (click)="saleActions.confirmDelete(sale)"
        mat-icon-button
        color="warn"
        class="!relative !z-10"
        [attr.aria-label]="'Delete ' + sale.item_name"
      >
        <mat-icon fontIcon="delete"></mat-icon>
      </button>
    </div>
  </div>
</article>
